import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { admin, AdminMessage, AdminMessageDetail, formatMoney } from "../../api/client";
import MessageStatusPill from "../../components/MessageStatusPill";
import { fmtDate, fmtNum, PageHeader, useFlash } from "./common";

const STATUSES = ["", "pending", "processing", "parsed", "skipped", "error"];
const PAGE = 100;

export function MessageInspector({ id, onClose, onReparse }: { id: string; onClose: () => void; onReparse?: () => void }) {
  const [msg, setMsg] = useState<AdminMessageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showJson, setShowJson] = useState<string | null>(null);

  useEffect(() => {
    admin.message(id).then(setMsg).catch((e) => setError(e.message));
  }, [id]);

  async function reparse() {
    try {
      await admin.reparseMessage(id);
      onReparse?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reparse failed");
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 920, width: "95vw", maxHeight: "90vh", overflow: "auto" }}>
        {error && <div className="page-error">{error}</div>}
        {!msg ? (
          <div className="page-empty">Loading…</div>
        ) : (
          <>
            <h2 style={{ marginTop: 0 }}>{msg.subject || "(no subject)"}</h2>
            <div className="detail-card">
              <div className="detail-row"><span className="label">From</span><span className="value">{msg.sender_name} &lt;{msg.sender_email}&gt;</span></div>
              <div className="detail-row"><span className="label">Mailbox</span><span className="value">{msg.account_email} (owner {msg.owner_email})</span></div>
              <div className="detail-row"><span className="label">Received</span><span className="value">{fmtDate(msg.received_at)}</span></div>
              <div className="detail-row"><span className="label">Result</span><span className="value"><MessageStatusPill status={msg.status} /> {msg.status_reason}</span></div>
              <div className="detail-row"><span className="label">Attachments</span><span className="value">{(msg.attachments ?? []).map((a) => a.filename).join(", ") || "—"}</span></div>
            </div>

            <h3>Receipts created</h3>
            {msg.receipts.length === 0 ? (
              <div className="muted">None.</div>
            ) : (
              <table className="data-table">
                <tbody>
                  {msg.receipts.map((r) => (
                    <tr key={r.id}>
                      <td>{r.vendor_name || "—"}</td>
                      <td>{r.receipt_type}</td>
                      <td>{formatMoney(r.total_cents, r.currency)}</td>
                      <td className="muted">{r.discarded ? `hidden: ${r.discard_reason}` : "visible"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3>AI calls</h3>
            {msg.ai_calls.length === 0 ? (
              <div className="muted">None (skipped before the AI).</div>
            ) : (
              <table className="data-table">
                <tbody>
                  {msg.ai_calls.map((c) => (
                    <tr key={c.id}>
                      <td>{fmtDate(c.created_at)}</td>
                      <td>{c.model}</td>
                      <td>{c.status}{c.error ? `: ${c.error}` : ""}</td>
                      <td>{fmtNum(c.input_tokens)} in / {fmtNum(c.output_tokens)} out</td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowJson(showJson === c.id ? null : c.id)}>
                          {showJson === c.id ? "Hide" : "Response"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {showJson && (
              <pre className="email-body" style={{ maxHeight: 300, overflow: "auto" }}>
                {JSON.stringify(msg.ai_calls.find((c) => c.id === showJson)?.response, null, 2)}
              </pre>
            )}

            <h3>Body</h3>
            {msg.body_html ? (
              <iframe className="email-iframe" sandbox="" srcDoc={msg.body_html} title="Email body" />
            ) : (
              <pre className="email-body">{msg.body_text || "(empty)"}</pre>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Close</button>
              <button className="btn btn-primary" onClick={reparse}>Reparse with AI</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const ownerUserId = params.get("owner_user_id") ?? undefined;
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<AdminMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inspect, setInspect] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const flash = useFlash(setToast);

  const load = () =>
    admin
      .messages({ status: status || undefined, q: q || undefined, owner_user_id: ownerUserId, limit: PAGE, offset })
      .then(setRows)
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, ownerUserId, offset]);

  function setStatus(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set("status", value);
    else next.delete("status");
    setParams(next, { replace: true });
    setOffset(0);
  }

  return (
    <div className="page">
      <PageHeader title="Ingest log" sub="Every email fetched by the worker, what the gate/AI decided, and why." />
      <div className="filter-bar">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <input type="search" placeholder="Subject or sender" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
        {ownerUserId && (
          <button className="btn btn-ghost btn-sm" onClick={() => { const n = new URLSearchParams(params); n.delete("owner_user_id"); setParams(n); }}>
            Clear user filter ✕
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
      </div>
      {error && <div className="page-error">{error}</div>}
      <table className="data-table">
        <thead>
          <tr>
            <th>Fetched</th>
            <th>Owner</th>
            <th>From</th>
            <th>Subject</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id} className="clickable" onClick={() => setInspect(m.id)}>
              <td style={{ whiteSpace: "nowrap" }}>{fmtDate(m.created_at)}</td>
              <td>{m.owner_email}</td>
              <td>{m.sender_email}</td>
              <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.subject}</td>
              <td>
                <MessageStatusPill status={m.status} />
                {m.status_reason && m.status_reason !== "force" && <div className="muted" style={{ fontSize: 12 }}>{m.status_reason.slice(0, 120)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Newer</button>
        <button className="btn btn-ghost btn-sm" disabled={rows.length < PAGE} onClick={() => setOffset(offset + PAGE)}>Older →</button>
      </div>
      {inspect && <MessageInspector id={inspect} onClose={() => setInspect(null)} onReparse={() => { flash("Queued for reparse."); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
